'use client';

import { CouponExperienceContentSchema, type TCouponExperienceContent } from '@shared/schemas';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TicketPercent, Building2, Check } from 'lucide-react';

type FormValue = Partial<TCouponExperienceContent>;

interface Props {
	value: FormValue;
	onChange: (value: FormValue) => void;
	onValidityChange: (valid: boolean) => void;
}

const COLOR_PALETTES = [
	{ name: 'Emerald Green', hex: '#059669' },
	{ name: 'Ruby Crimson', hex: '#e11d48' },
	{ name: 'Warm Amber', hex: '#d97706' },
	{ name: 'Royal Violet', hex: '#7c3aed' },
	{ name: 'Ocean Blue', hex: '#1d4ed8' },
	{ name: 'Midnight Slate', hex: '#0f172a' },
];

export function CouponForm({ value, onChange, onValidityChange }: Props) {
	const updateFields = (patch: Partial<TCouponExperienceContent>) => {
		const updated = {
			type: 'coupon' as const,
			company: value.company ?? '',
			title: value.title ?? '',
			couponCode: value.couponCode ?? '',
			accentColor: value.accentColor ?? '#059669',
			...value,
			...patch,
		};
		onChange(updated);
		onValidityChange(CouponExperienceContentSchema.safeParse(updated).success);
	};

	return (
		<div className="space-y-6">
			{/* Brand & Offer Information */}
			<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-5">
				<div className="flex items-center gap-3 border-b border-slate-100 pb-3">
					<div className="flex size-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
						<TicketPercent className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">Promotion & Offer Details</h2>
						<p className="text-xs text-slate-500">
							Create an attractive discount voucher or promotional coupon
						</p>
					</div>
				</div>

				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-1.5">
						<Label htmlFor="couponCompany" className="text-xs font-semibold text-slate-700">
							Business / Store Name <span className="text-rose-500">*</span>
						</Label>
						<Input
							id="couponCompany"
							value={value.company ?? ''}
							onChange={(e) => updateFields({ company: e.target.value })}
							placeholder="e.g. Bella & Bloom Boutique"
							className="h-10 text-sm font-medium"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="couponDiscount" className="text-xs font-semibold text-slate-700">
							Discount Badge / Tag
						</Label>
						<Input
							id="couponDiscount"
							value={value.discount ?? ''}
							onChange={(e) => updateFields({ discount: e.target.value })}
							placeholder="e.g. 20% OFF or $15 VOUCHER"
							className="h-10 text-sm font-semibold"
						/>
					</div>

					<div className="space-y-1.5 sm:col-span-2">
						<Label htmlFor="couponTitle" className="text-xs font-semibold text-slate-700">
							Offer Headline <span className="text-rose-500">*</span>
						</Label>
						<Input
							id="couponTitle"
							value={value.title ?? ''}
							onChange={(e) => updateFields({ title: e.target.value })}
							placeholder="e.g. 20% Off Everything in Store This Weekend"
							className="h-10 text-sm font-medium"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="couponCode" className="text-xs font-semibold text-slate-700">
							Promo / Coupon Code <span className="text-rose-500">*</span>
						</Label>
						<Input
							id="couponCode"
							value={value.couponCode ?? ''}
							onChange={(e) => updateFields({ couponCode: e.target.value.toUpperCase() })}
							placeholder="e.g. SAVE20"
							className="h-10 text-sm font-mono font-bold tracking-wider uppercase"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="couponValidUntil" className="text-xs font-semibold text-slate-700">
							Expiration Date / Validity
						</Label>
						<Input
							id="couponValidUntil"
							value={value.validUntil ?? ''}
							onChange={(e) => updateFields({ validUntil: e.target.value })}
							placeholder="e.g. Valid until Dec 31, 2026"
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-1.5 sm:col-span-2">
						<Label htmlFor="couponTerms" className="text-xs font-semibold text-slate-700">
							Terms & Conditions
						</Label>
						<Textarea
							id="couponTerms"
							value={value.terms ?? ''}
							onChange={(e) => updateFields({ terms: e.target.value })}
							placeholder="e.g. Valid on dine-in and takeout orders over $40. Limit 1 coupon per customer. Cannot be combined with other offers."
							rows={3}
							className="text-sm resize-none"
						/>
					</div>
				</div>

				{/* Theme Color Picker */}
				<div className="pt-3 border-t border-slate-100 space-y-2">
					<Label className="text-xs font-semibold text-slate-700">Theme Color</Label>
					<div className="flex flex-wrap items-center gap-2">
						{COLOR_PALETTES.map((palette) => {
							const selected = (value.accentColor ?? '#059669') === palette.hex;
							return (
								<button
									key={palette.hex}
									type="button"
									onClick={() =>
										updateFields({
											accentColor: palette.hex,
											headerColor: palette.hex,
										})
									}
									className="relative size-8 rounded-full transition-transform hover:scale-110 cursor-pointer"
									style={{ backgroundColor: palette.hex }}
									title={palette.name}
								>
									{selected && (
										<Check className="absolute inset-0 m-auto size-4 text-white stroke-[3]" />
									)}
								</button>
							);
						})}
					</div>
				</div>
			</div>

			{/* How to Redeem & Contact */}
			<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-4">
				<div className="flex items-center gap-3 border-b border-slate-100 pb-3">
					<div className="flex size-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
						<Building2 className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">Where & How to Redeem</h2>
						<p className="text-xs text-slate-500">
							Provide links to redeem the offer online or visit your physical location
						</p>
					</div>
				</div>

				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-1.5">
						<Label htmlFor="couponWebsite" className="text-xs font-semibold text-slate-700">
							Store / Shop Website (Redeem Online)
						</Label>
						<Input
							id="couponWebsite"
							type="url"
							value={value.website ?? ''}
							onChange={(e) => updateFields({ website: e.target.value })}
							placeholder="https://example.com/shop"
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="couponPhone" className="text-xs font-semibold text-slate-700">
							Store Phone
						</Label>
						<Input
							id="couponPhone"
							type="tel"
							value={value.phone ?? ''}
							onChange={(e) => updateFields({ phone: e.target.value })}
							placeholder="+1 (555) 000-1122"
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-1.5 sm:col-span-2">
						<Label htmlFor="couponAddress" className="text-xs font-semibold text-slate-700">
							Store Address (Redeem In-Person)
						</Label>
						<Input
							id="couponAddress"
							value={value.address ?? ''}
							onChange={(e) => updateFields({ address: e.target.value })}
							placeholder="e.g. 789 Broadway Ave, New York, NY"
							className="h-10 text-sm"
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
